import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { RiEdit2Line, RiLoader4Line, RiOrganizationChart } from 'react-icons/ri';
import { patch } from '@/api/api.client';
import { Button } from '@/components/primitives/button';
import { Input } from '@/components/primitives/input';
import { showErrorToast, showSuccessToast } from '@/components/primitives/sonner-helpers';
import { QueryKeys } from '@/utils/query-keys';
import { useOrganization } from './organization.resource';

/**
 * Organization settings for the community self-hosted build — renames the organization through
 * `PATCH /v1/organizations`, the only organization mutation the community API exposes.
 */
export function OrganizationSettings() {
  const queryClient = useQueryClient();
  const { organization, isLoaded } = useOrganization() as {
    organization?: { _id?: string; name?: string };
    isLoaded: boolean;
  };

  const [isEditingName, setIsEditingName] = useState(false);
  const [organizationName, setOrganizationName] = useState(organization?.name || '');
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    if (!isEditingName) {
      setOrganizationName(organization?.name || '');
    }
  }, [organization?.name, isEditingName]);

  const handleUpdateName = async (e: React.FormEvent) => {
    e.preventDefault();

    const name = organizationName.trim();
    if (!name || name === organization?.name) {
      setIsEditingName(false);

      return;
    }

    setIsUpdating(true);
    try {
      await patch('/organizations', { body: { name } });
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.myOrganization] });
      showSuccessToast('Organization name updated successfully', 'Organization Updated');
      setIsEditingName(false);
    } catch (err: any) {
      showErrorToast(err.message || 'Failed to update organization', 'Update Error');
      setOrganizationName(organization?.name || '');
    } finally {
      setIsUpdating(false);
    }
  };

  if (!isLoaded) {
    return (
      <div className="flex items-center justify-center py-12">
        <RiLoader4Line className="size-6 animate-spin text-foreground-600" />
      </div>
    );
  }

  if (!organization) {
    return (
      <div className="py-12 text-center">
        <p className="text-sm text-foreground-600">No organization data available</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-neutral-200 bg-white p-4">
        <div className="mb-3 flex items-center gap-2">
          <RiOrganizationChart className="size-5 text-foreground-600" />
          <h3 className="text-sm font-medium text-foreground-950">Organization Name</h3>
        </div>

        {isEditingName ? (
          <form onSubmit={handleUpdateName} className="space-y-3">
            <Input
              type="text"
              value={organizationName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setOrganizationName(e.target.value)}
              placeholder="Enter organization name"
              required
              disabled={isUpdating}
              className="h-10"
              autoFocus
            />
            <div className="flex gap-2">
              <Button
                type="submit"
                disabled={isUpdating || !organizationName.trim()}
                variant="primary"
                mode="filled"
                size="sm"
                className="h-9"
              >
                {isUpdating ? <RiLoader4Line className="size-4 animate-spin" /> : 'Save'}
              </Button>
              <Button
                type="button"
                onClick={() => {
                  setIsEditingName(false);
                  setOrganizationName(organization.name || '');
                }}
                disabled={isUpdating}
                variant="secondary"
                mode="outline"
                size="sm"
                className="h-9"
              >
                Cancel
              </Button>
            </div>
          </form>
        ) : (
          <div className="flex items-center justify-between">
            <div className="flex flex-col">
              <span className="text-sm font-medium text-foreground-950">{organization.name}</span>
              <span className="text-xs text-foreground-600">ID: {organization._id}</span>
            </div>
            <Button
              onClick={() => setIsEditingName(true)}
              variant="secondary"
              mode="ghost"
              size="sm"
              className="h-8 gap-1.5"
            >
              <RiEdit2Line className="size-4" />
              Edit
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
